import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const STORAGE_URL = 'https://ymjeinhylrufskwhzobk.supabase.co/storage/v1/object/public/bible/rvr1960';

interface BookData {
  abbrev: string;
  name: string;
  chapters: string[][];
}

const BOOKS_MAP: Record<string, string> = {
  genesis: 'Génesis', exodo: 'Éxodo', levitico: 'Levítico', numeros: 'Números',
  deuteronomio: 'Deuteronomio', josue: 'Josué', jueces: 'Jueces', rut: 'Rut',
  '1-samuel': '1 Samuel', '2-samuel': '2 Samuel', '1-reyes': '1 Reyes',
  '2-reyes': '2 Reyes', '1-cronicas': '1 Crónicas', '2-cronicas': '2 Crónicas',
  esdras: 'Esdras', nehemias: 'Nehemías', ester: 'Ester', job: 'Job',
  salmos: 'Salmos', proverbios: 'Proverbios', eclesiastes: 'Eclesiastés',
  cantares: 'Cantares', isaias: 'Isaías', jeremias: 'Jeremías',
  lamentaciones: 'Lamentaciones', ezequiel: 'Ezequiel', daniel: 'Daniel',
  oseas: 'Oseas', joel: 'Joel', amos: 'Amos', abdias: 'Abdías',
  jonas: 'Jonás', miqueas: 'Miqueas', nahum: 'Nahum', habacuc: 'Habacuc',
  sofonias: 'Sofonías', hageo: 'Hageo', zacarias: 'Zacarías', malaquias: 'Malaquías',
  mateo: 'Mateo', marcos: 'Marcos', lucas: 'Lucas', juan: 'Juan',
  hechos: 'Hechos', romanos: 'Romanos', '1-corintios': '1 Corintios',
  '2-corintios': '2 Corintios', galatas: 'Gálatas', efesios: 'Efesios',
  filipenses: 'Filipenses', colosenses: 'Colosenses', '1-tesalonicenses': '1 Tesalonicenses',
  '2-tesalonicenses': '2 Tesalonicenses', '1-timoteo': '1 Timoteo',
  '2-timoteo': '2 Timoteo', tito: 'Tito', filemon: 'Filemón',
  hebreos: 'Hebreos', santiago: 'Santiago', '1-pedro': '1 Pedro',
  '2-pedro': '2 Pedro', '1-juan': '1 Juan', '2-juan': '2 Juan',
  '3-juan': '3 Juan', judas: 'Judas', apocalipsis: 'Apocalipsis',
};

export default function BibleBook() {
  const { book } = useParams<{ book: string }>();
  const [data, setData] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bookName = BOOKS_MAP[book || ''] || book;

  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${STORAGE_URL}/${book}.json`);
        if (!response.ok) throw new Error('No se pudo cargar el libro');
        const json: BookData = await response.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };
    if (book) fetchBook();
  }, [book]);

  if (loading) {
    return (
      <div>
        <div className="bg-primary text-white p-4 flex items-center gap-4">
          <Link to="/bible" className="text-white">←</Link>
          <div className="h-6 bg-white/20 rounded w-32 animate-pulse"></div>
        </div>
        <div className="p-4 grid grid-cols-5 gap-3">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="aspect-square bg-[#3A3A3C] rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error || 'Error al cargar'}</p>
        <Link to="/bible" className="text-primary mt-4 inline-block">← Volver</Link>
      </div>
    );
  }

  const totalChapters = data.chapters.length;

  return (
    <div>
      <div className="bg-primary text-white p-4 flex items-center gap-4">
        <Link to="/bible" className="text-white text-xl">←</Link>
        <h1 className="text-xl font-bold">{bookName}</h1>
      </div>

      <div className="p-4">
        <p className="text-[#B3B3B3] mb-4 text-sm">Selecciona un capítulo</p>
        
        <div className="grid grid-cols-5 gap-3">
          {Array.from({ length: totalChapters }, (_, i) => i + 1).map((chapterNum) => (
            <Link
              key={chapterNum}
              to={`/bible/${book}/${chapterNum}`}
              className="aspect-square flex items-center justify-center border-2 border-primary text-primary font-semibold rounded hover:bg-primary hover:text-white transition-colors"
            >
              {chapterNum}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
